import { analytics } from '../../../../Services/analytics';
import {DashboardReportingSections} from "./DashboardReportingSections";
import {EEScorecardAtom} from '../../../../State/EEScorecard.Atom';
import {useEffect, useState} from "react";
import {useRecoilValue} from "recoil";
import {TextField, Typography} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DataTable} from "../../../../Component/Common/Components/DataTable/DataTable";
import {ColumnSortOption} from "../../../../Component/Model/Dto/ColumnSortOption";
import {EntriesPerPage} from "../../../../Component/Model/Table/Table";
import {DashboardReportsTableMapper} from "./DashboardReportingTableColumns";
import {EEDashboardReportingTable} from "../../../../Component/Model/Dto/EmploymentEquity/DashboardReporting";
import {defaultObject} from "../../../../Component/Model/helpers";
import React from "react";
import {toast} from "react-toastify";
import {
    EEDashboardReportsService
} from "../../../../Component/Api/EmploymentEquity/EEDashboardReports/EEDashboardReportService";
import {ScorecardAtom} from "../../../../State/Scorecard.Atom";
import {ConfirmationDialog} from "../../../../Component/Common/Components/ConfirmationDialog";
import {MDTypography} from "../../../../Component/Common/Components/MDTypography";
import config from "../../../../config";
import {LoadingButton} from "@mui/lab";
import {getDateWithoutTime, userFriendlyDate} from "../../../../Helpers/DateFormatter";
import { UserActivityService } from "../../../../Component/Api/UserActivity/UserActivityService";
import { UserActivityActionType } from "../../../../Component/Model/Enum/BEE123Enums";
import { DashboardReportingSection } from "../../../../Component/Common/Constants/UserActivityTrackingSections";
import { ValidatedMobileDatePicker } from "../../../../Component/Common/Components/ValidatedMobileDatePicker/ValidatedMobileDatePicker";

export const DashboardPage = () => {
    const [reportSections, setReportSections] = useState(DashboardReportingSections);
    const [dashboardReportData, setDashboardReportData] = useState<EEDashboardReportingTable.DashboardReportingDto[]>(defaultObject);

    const eeScorecard = useRecoilValue(EEScorecardAtom);
    const scorecard = useRecoilValue(ScorecardAtom);

    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({
        defaultValue: 10,
    });

    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const [selectedReportId, setSelectedReportId] = React.useState("");
    const [fileType, setFileType] = React.useState(40);

    const [counter, setCounter] = React.useState(0);
    const confirmDeleteMessage = "This action cannot be reversed. Are you sure you want to delete this report?";
    const [isGeneratingReport, setIsGeneratingReport] = React.useState<boolean>(false);

    const [isAllSelected, setIsAllSelected] = useState(false);
    const [isReportContentSelected, setIsReportContentSelected] = useState (false);
    const [isGenerateButtonEnabled, setIsGenerateButtonEnabled] = useState (false);

    const [filterDates, setFilterDates] = useState<Record<number, Date>>(() => {
        const defaultDate = new Date(eeScorecard.ReportPeriodDateTo);
        const initialDates: Record<number, Date> = {};

        reportSections.forEach(section => {
            if (section.Date.Type === 'InputDate') {
                initialDates[section.Id] = defaultDate;
            }
        });

        return initialDates;
    });

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedReportId("");
    };

    const handleCheckboxChange = (identifier: string) => {
        setReportSections((prevSections) => {
            const updatedSections = prevSections.map((section) =>
                section.CheckboxIdentifier === identifier
                    ? { ...section, Selected: !section.Selected }
                    : section
            );
            const anyReportContentSelected = updatedSections.some(section => section.Selected);
            setIsReportContentSelected(anyReportContentSelected); 
            setIsGenerateButtonEnabled(anyReportContentSelected);

            return updatedSections;       
        });
    };

    const handleSelectAllChange = () => {
        const newSelection = !isAllSelected;
        setIsAllSelected(newSelection);
        setReportSections((prevSections) =>
            prevSections.map((section) => ({
                ...section,
                Selected: newSelection,
            }))
        );
        setIsReportContentSelected(newSelection);
        setIsGenerateButtonEnabled(newSelection);
    };

    const generateReport = async () => {
        try {
            setIsGeneratingReport(true);

            const selectedReportSections: EEDashboardReportingTable.DashboardReportSections[] = reportSections
                .filter((section) => section.Selected)
                .map((section) => ({
                    reportContent: section.Id,
                    filterDate:  filterDates[section.Id]
                }));

            const request: EEDashboardReportingTable.DashboardPowerpointDtoIn = {
                scorecardId: scorecard.scorecardId,
                selectedReportSections
            };

            const response = await EEDashboardReportsService.generateReport(request);
            UserActivityService.createActivityLog({
                section: DashboardReportingSection,
                actionType: UserActivityActionType.Generate,
                actionDescription: "Generate dashboard report",
                scorecardId: scorecard.scorecardId,
                requestUrl: window.location.href
            });
            analytics.eeDashboardPowerpointGenerated();
            window.open(response.data, '_blank');
        } catch (error: any) {
            if (error.response.status === 400) {
                toast.error(error.response.data);
            } else {
                toast.error("Unable to Generate Report");
            }
        } finally {
            fetchData();
            setIsGeneratingReport(false);
        }
    };

    const handleDownloadReport = async (id: string) => {
        try {
            const response = await EEDashboardReportsService.downloadReport(+id);
            window.open(response.data, '_blank');
        } catch (error: any) {
            if (error.response != null) {
                toast.error(error.response.data);
            }
        }
    };

    const handleConfirmDelete = async (id: string) => {
        setSelectedReportId(id);
        setShowConfirmationDialog(true);
    };

    const handleDeleteReport = async () => {
        try {
            await EEDashboardReportsService.deleteReport(+selectedReportId);
            resetWizard();
            fetchData();
        } catch (error) {
            console.log(error);
        }
        handleCloseConfirmation();
    };

    const fetchData = async () => {
        setIsLoading(true);

        try {
            const EEDashboardReportSearch: EEDashboardReportingTable.SearchRequest = {
                pageNumber: pageNumber,
                scorecardId: scorecard.scorecardId,
                searchValue: searchValue,
                pageSize: entriesPerPage.defaultValue!
            };

            const response = await EEDashboardReportsService.searchDashboardReports(EEDashboardReportSearch);
            const reportData = response.data as unknown as EEDashboardReportingTable.DashboardReportingDto[];

            setDashboardReportData(reportData);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetWizard = () => {
        setFileType(fileType);
        setCounter(counter + 1);
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({defaultValue: entriesPerPage});
        setPageNumber(1);
    };

    const pageNumberChanged = (updatedpageNumber: number) => {
        setPageNumber(updatedpageNumber);
    };

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    };

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    };

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue]);

    const tableData = DashboardReportsTableMapper(
        dashboardReportData,
        handleDownloadReport,
        handleConfirmDelete
    );

    return (
        <div className="row">
            <div className="col-md-12">
                {config.enable_new_EE_Reporting_Dashboards && (
                    <>
                        <MDTypography variant="h5" fontWeight="bold" sx={{mb: 1}}>
                            Report Content
                        </MDTypography>

                        <div className="table-responsive">
                            <table className="table table-striped table-bordered pull-left detailTable">
                                <thead>
                                    <tr>
                                        <th style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderColor: 'black' }}>
                                            <input
                                                type="checkbox"
                                                className="large-check-box"
                                                onClick={() => handleSelectAllChange()}
                                            />
                                            <span style={{ flexGrow: 1, textAlign: 'center' }}>Select</span>
                                        </th>
                                        <th align="left">Report Section</th>
                                    </tr>
                                </thead>
                                <tbody>
                                {reportSections.map((section) => (
                                    <tr key={section.CheckboxIdentifier}>
                                        <td align="left">
                                            <input
                                                type="checkbox"
                                                className="large-check-box"
                                                checked={section.Selected}
                                                id={section.CheckboxIdentifier}
                                                onChange={() => handleCheckboxChange(section.CheckboxIdentifier)}
                                            />
                                        </td>
                                        <td align="left">{section.Name}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>

                            {isReportContentSelected &&
                                <MDTypography variant="h5" fontWeight="bold" sx={{mb: 1}}>
                                    Report Details
                                </MDTypography>
                            }
                            
                            {reportSections.map(
                                (section) =>
                                    section.Selected && (
                                        <div key={section.CheckboxIdentifier} className="report-detail"
                                             style={{display: 'flex', alignItems: 'center', marginBottom: '1.5rem'}}>
                                            <Typography
                                                variant="body2"
                                            >
                                                {section.Name} - {section.Date.LabelText} {section.Date.Type === "EndDate"
                                                ? userFriendlyDate(eeScorecard.ReportPeriodDateTo)
                                                : section.Date.Type === "ReportingDate"
                                                    ? userFriendlyDate(eeScorecard.ReportPeriodDateFrom) + ' to ' + userFriendlyDate(eeScorecard.ReportPeriodDateTo)
                                                    : ''}
                                            </Typography>

                                            {section.Date.Type === "InputDate" && (
                                                <div>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        <ValidatedMobileDatePicker
                                                            inputFormat="yyyy/MM/dd"
                                                            mask="____/__/__"
                                                            value={filterDates[section.Id]}
                                                            onChange={(date: Date) => {
                                                                if (date) {
                                                                    setFilterDates(prev => ({
                                                                        ...prev,
                                                                        [section.Id]: getDateWithoutTime(date)
                                                                    }));
                                                                }
                                                            }}
                                                            renderInput={(params: any) => <TextField
                                                                {...params}
                                                                size="small"
                                                                style={{
                                                                paddingLeft: 5
                                                            }}/>}
                                                            views={["year", "month", "day"]}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                            )}
                                        </div>
                                    )
                            )}
                            <div style={{paddingBottom: 25}}>
                                <LoadingButton color="primary" variant="contained" type="submit"
                                               onClick={() => generateReport()} loading={isGeneratingReport}
                                               disabled={!isGenerateButtonEnabled}>
                                    Generate
                                </LoadingButton>
                            </div>

                            <MDTypography variant="h5" fontWeight="bold">
                                Reports Generated
                            </MDTypography>

                            <DataTable
                                key={"DashboardReportingTable"}
                                table={tableData}
                                canSearch={false}
                                showEntriesPerPage={false}
                                showTotalEntries={false}
                                entriesPerPage={entriesPerPage}
                                pageNumber={pageNumber}
                                filteredItemsCount={filteredItemsCount}
                                totalItemsCount={totalItemsCount}
                                entriesPerPageChanged={entriesPerPageChanged}
                                pageNumberChanged={pageNumberChanged}
                                searchValueChanged={searchValueChanged}
                                isLoading={isLoading}
                                columnSortingChanged={columnSortingChanged}
                            />
                        </div>

                        <ConfirmationDialog
                            showDialog={showConfirmationDialog}
                            handleAccept={handleDeleteReport}
                            handleCancel={handleCloseConfirmation}
                            heading={"Confirm Delete"}
                            message={confirmDeleteMessage}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
